// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLanguages } from "@insite/client-framework/Store/Context/ContextSelectors";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Globe from "@insite/mobius/Icons/Globe";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
// import React, { FC } from "react"; // Starter Custom Code
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

/* Starter Custom Code - Start */
import getColor from "@insite/mobius/utilities/getColor";
import SelectInline, { SelectInlineStyles } from "@store/Library/Components/Toolkit/SelectInline";
import { menuSecondaryNavigationItemStyles } from "@store/Themes/common/mixins/menu";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { iconScalableStyles } from "@toolkit/Themes/common/mixins";
import React, { FC, useMemo } from "react";

/* Store Custom Code - Start */
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
/* Store Custom Code - End */

const iconSize = pxToRem(18);
const iconOffset = pxToRem(0);
/* Starter Custom Code - End */

const enum fields {
    showIcon = "showIcon",
}

const mapStateToProps = (state: ApplicationState) => ({
    languages: getLanguages(state),
    currentLanguage: state.context.session.language,
});

const mapDispatchToProps = {
    setLanguage,
};

export interface LanguageMenuStyles {
    languageWrapper?: InjectableCss;
    languageIcon?: IconPresentationProps;
    languageSelect?: SelectPresentationProps;
    // Starter Custom Code
    selectInline?: SelectInlineStyles;
}

export const languageMenuStyles: LanguageMenuStyles = {
    languageWrapper: {
        css: css`
            ${menuSecondaryNavigationItemStyles()}
            display: flex;
            align-items: center;

            &:hover {
                color: ${getColor("primary")}; /* Store Custom Code */
                text-decoration: underline;
                transition: ${transitionHoverStyles()};
            }
            /* Store Custom Code - Start */
            ${({ theme }) => css`
                font-size: ${theme.design.typography.size.smaller};

                ${breakpoint("lg")} {
                    font-size: ${theme.design.typography.size.small};
                }

                ${breakpoint("xl")} {
                    font-size: ${theme.design.typography.size.base};
                }
            `}/* Store Custom Code - End */
        `,
    },
    languageIcon: {
        src: Globe,
        color: "inherit",
        css: css`
            ${iconScalableStyles(iconSize, false)}
            margin-right: ${x => x.theme.design.menu.secondaryNavigation.iconSpacing};
            position: absolute;
        `,
    },
    selectInline: {
        select: {
            cssOverrides: {
                inputSelect: css`
                    font-weight: inherit;
                    line-height: inherit;
                    cursor: pointer;

                    &:hover {
                        text-decoration: underline;
                    }
                `,
            },
        },
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showIcon]: boolean;
    };
}

/* Starter Custom Code - Start */
export const LogoImage = styled.img`
    width: ${iconSize};
    height: ${iconSize};
    background: currentColor;
    position: absolute;
    border-radius: 50%;
    object-fit: cover;
    overflow: hidden;
`;
/* Starter Custom Code - End */

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
export const LanguageMenu: FC<Props> = ({ languages, currentLanguage, fields, setLanguage }) => {
    if (!languages || languages.length <= 1) {
        return null;
    }

    const styles = useMergeStyles("languageMenu", languageMenuStyles);

    const menuId = "languageMenu";

    /* Starter Custom Code - Start */
    const selectInlineStyles = useMemo(() => {
        if (fields.showIcon) {
            return mergeProps<SelectInlineStyles>(styles.selectInline, {
                select: {
                    cssOverrides: {
                        inputSelect: css`
                            ${({ theme }) => {
                                const iconSpacing = theme.design.menu.secondaryNavigation.iconSpacing;
                                return `padding-left: calc(${iconSize} + ${iconSpacing} - (${iconOffset} * 2)) !important;`;
                            }}
                        `,
                    },
                },
            });
        }

        return styles.selectInline;
    }, [styles, fields.showIcon]);
    /* Starter Custom Code - End */

    return (
        <StyledWrapper {...styles.languageWrapper}>
            {fields.showIcon &&
                (currentLanguage?.imageFilePath ? (
                    <LogoImage
                        src={currentLanguage.imageFilePath}
                        alt={currentLanguage.languageCode} // Starter Custom Code
                        aria-hidden="true" // Starter Custom Code
                    />
                ) : (
                    <Icon {...styles.languageIcon} />
                ))}
            <VisuallyHidden as="label" htmlFor={menuId} id={`${menuId}-label`}>
                {translate("Language")}
            </VisuallyHidden>
            <SelectInline // Starter Custom Code
                extendedStyles={selectInlineStyles} // Starter Custom Code
                uid={menuId}
                data-test-selector="languageSelector"
                onChange={event => setLanguage({ languageId: event.currentTarget.value })}
                value={currentLanguage?.id}
                hasLabel
            >
                {languages.map(c => (
                    <option value={c.id} key={c.id}>
                        {c.description}
                    </option>
                ))}
            </SelectInline>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(LanguageMenu),
    definition: {
        displayName: "Language Menu",
        icon: "language",
        fieldDefinitions: [
            {
                name: fields.showIcon,
                displayName: "Show Icon",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
